import { useEffect } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, Heart } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TOOL_BY_SLUG, toolPath } from "@/data/tools";
import { ARTICLES } from "@/data/articles";

const ARTICLE_BY_SLUG = Object.fromEntries(ARTICLES.map((a) => [a.slug, a]));
import { track } from "@/lib/toolTrack";
import { TimedTool } from "@/components/tools/TimedTool";
import { GroundTool } from "@/components/tools/GroundTool";
import { WorryTool } from "@/components/tools/WorryTool";
import { StressTool } from "@/components/tools/StressTool";
import { CompassionTool } from "@/components/tools/CompassionTool";
import { AngerTool } from "@/components/tools/AngerTool";

const SITE = "https://mindfulmeyourcalmcompanion.com";
const RUNNERS = { box: TimedTool, breath: TimedTool, scan: TimedTool, ground: GroundTool, worry: WorryTool, stress: StressTool, compassion: CompassionTool, anger: AngerTool };

const useFaqSchema = (t) => {
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: t.faqs.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    document.head.appendChild(s);
    return () => s.remove();
  }, [t]);
};

const Related = ({ t }) => (
  <section className="mt-14" data-testid="tool-related">
    <h2 className="text-xl font-bold">Related free tools</h2>
    <div className="grid sm:grid-cols-3 gap-3 mt-4">
      {t.related.map((s) => TOOL_BY_SLUG[s]).map((r) => (
        <Link key={r.slug} to={toolPath(r)} className="mm-card mm-card-hover p-4" data-testid={`tool-related-${r.slug}`}>
          <div className="text-2xl" aria-hidden>{r.emoji}</div><div className="font-bold mt-2">{r.name}</div><div className="text-xs text-mm-muted mt-1">{r.mins} · {r.helps}</div>
        </Link>
      ))}
    </div>
    {t.articles.length > 0 && (
      <div className="mt-6 text-sm" data-testid="tool-articles">
        <span className="font-semibold">Read more: </span>
        {t.articles.map((slug) => ARTICLE_BY_SLUG[slug]).filter(Boolean).map((a) => <Link key={a.slug} to={`/articles/${a.slug}`} className="text-mm-primary font-semibold mr-3">{a.title}</Link>)}
      </div>
    )}
  </section>
);

export default function ToolPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const t = TOOL_BY_SLUG[slug];
  const shared = params.get("shared") === "1";
  usePageMeta({ title: t?.title || "Free calm tools | Mindful Me", description: t?.description || "", path: `/tools/${slug}`, image: `${SITE}/api/tools/og/${slug}.png` });
  useEffect(() => { if (t && !t.to) { track(slug, "visit"); if (shared) track(slug, "shared_visit"); } }, [slug, t, shared]);
  if (!t) return <Navigate to="/tools" replace />;
  if (t.to) return <Navigate to={t.to} replace />;
  return <ToolBody t={t} shared={shared} />;
}

const ToolBody = ({ t, shared }) => {
  useFaqSchema(t);
  const Runner = RUNNERS[t.kind];
  return (
    <PublicLayout>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-20" data-testid={`tool-page-${t.slug}`}>
        <Link to="/tools" className="text-sm text-mm-muted hover:text-mm-primary" data-testid="tool-back-hub">← Free calm tools</Link>
        {shared && <div className="mm-soft rounded-2xl px-4 py-3 mt-4 text-sm flex items-center gap-2 mm-fade-up" data-testid="tool-shared-banner"><Heart size={15} className="text-[#D94F7A] fill-current" />A friend thought you might like this.</div>}
        <h1 className="text-3xl sm:text-4xl font-bold mt-4">{t.emoji} {t.name}</h1>
        <p className="text-mm-muted mt-2">{t.mins} · Free · No sign-up · {t.helps}</p>
        <div className={`rounded-[32px] p-6 sm:p-10 mt-6 border border-mm-primary/10 ${t.night ? "bg-gradient-to-br from-[#E8E1FA] to-[#D6E4F5]" : "bg-gradient-to-br from-[#F3EEFC] to-[#E8F4F8]"}`} data-testid="tool-runner">
          <Runner tool={t} />
        </div>
        <section className="mt-12" data-testid="tool-how">
          <h2 className="text-xl font-bold">How and why it works</h2>
          {t.how.map((p) => <p key={p.slice(0, 20)} className="mt-3 leading-relaxed">{p}</p>)}
        </section>
        <section className="mt-10" data-testid="tool-faqs">
          <h2 className="text-xl font-bold">Questions people ask</h2>
          <div className="mt-3 space-y-2">
            {t.faqs.map(([q, a], i) => (
              <details key={q} className="mm-card p-4 group" data-testid={`tool-faq-${i}`}>
                <summary className="font-semibold cursor-pointer list-none flex justify-between gap-3">{q}<ArrowRight size={16} className="shrink-0 transition-transform group-open:rotate-90" /></summary>
                <p className="mt-2 text-sm text-mm-muted leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>
        <Related t={t} />
      </article>
    </PublicLayout>
  );
};
